import { useCallback, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { Envelope } from '@storefront/lib-global';
import { sendApplyProductVariantEvent } from '@storefront/lib-global/utils/gtm/events/apply_product_variant/sendApplyProductVariantEvent';
import { useSizeAndWidthContext } from '../../../context';
import { useFilteredWidths, useLinePositions } from '../../../hooks';
import { hasBackInStockFlag, isOutOfStock } from '../../../utils';
import { MeasurementBlock, MeasurementList, MeasurementListItem } from '../MeasurementBlock/MeasurementBlock';
import { CrossedLine } from './Widths.styles';

export function Widths() {
  const { handleChangeWidth, selectedWidth, selectedSize, uniqueWidths, variants } = useSizeAndWidthContext();
  const { lineRefs, crossLineRefs, assignLineRef, assignCrossLineRef, updateLinePositions } = useLinePositions();

  const { t } = useTranslation(['lib-products-common']);
  const newUniqueWidths = useFilteredWidths();

  const handleOnClick = (productWidth: string) => () => {
    const updateWidth = handleChangeWidth(productWidth);
    const sku = updateWidth();

    sendApplyProductVariantEvent({
      variant_name: 'Fit',
      variant_value: `Fit ${productWidth}`,
      item_sku: sku as unknown as string,
    });
  };

  const renderNotificationsDisabledCrossedLine = useCallback(
    (index: number, backInStockNotification: boolean, isOOS: boolean) => {
      const shouldRenderCrossedLine = !backInStockNotification && isOOS;

      if (!shouldRenderCrossedLine) return null;

      return <CrossedLine ref={assignCrossLineRef(index)} data-testid="notificationsDisabled" className="inverted" />;
    },
    [assignCrossLineRef],
  );

  useEffect(() => {
    const handleResize = () => {
      // updated diagonal line for crossed out size
      updateLinePositions(lineRefs);
      // updated diagonal line for variant with notify me disabled
      updateLinePositions(crossLineRefs, true);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedSize, selectedWidth, lineRefs, updateLinePositions, crossLineRefs]);

  return (
    <MeasurementList>
      {newUniqueWidths.map((productWidth, index) => {
        const isOOS = isOutOfStock(selectedSize, productWidth, variants);
        const isWidthUnavailable = isOOS ? 'crossed' : '';
        const isActive = productWidth === selectedWidth;
        const backInStockNotification =
          isOOS && selectedSize ? hasBackInStockFlag(selectedSize, productWidth, variants) : false;

        return (
          <MeasurementListItem key={productWidth} className={isWidthUnavailable} isTwoSpaces>
            <MeasurementBlock
              className={isActive || uniqueWidths.length === 1 ? `active ${isWidthUnavailable}` : isWidthUnavailable}
              type="button"
              aria-label={`${t('width.selector.button.label', { width: productWidth })}${
                isWidthUnavailable ? `, ${t('width.selector.button.unavailable')}` : ''
              }`}
              aria-pressed={isActive || uniqueWidths.length === 1}
              onClick={handleOnClick(productWidth)}
              title={productWidth}
              data-testid="widthItem"
            >
              {productWidth}
              {backInStockNotification && <Envelope />}
              {isWidthUnavailable && <CrossedLine ref={assignLineRef(index)} data-testid="crossedOut" />}
              {renderNotificationsDisabledCrossedLine(index, backInStockNotification, isOOS)}
            </MeasurementBlock>
          </MeasurementListItem>
        );
      })}
    </MeasurementList>
  );
}
